import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Camera, Cpu, PiggyBank, ScanLine, Database, CloudRain, Scale, ShieldCheck } from "lucide-react";
import Seo from "@/components/site/Seo";
import SavingsCalculator from "@/components/site/SavingsCalculator";
import { BottomWave } from "@/components/site/primitives";
import { CTABand } from "@/pages/BusinessWaste";
import { useI18n } from "@/i18n";

const STEPS = [
  { icon: Camera, title: "Snap a photo", desc: "Take a picture of your current waste or dumpster invoice — any hauler." },
  { icon: Cpu, title: "AI reads it", desc: "Our AI instantly finds your current monthly charge, no typing needed." },
  { icon: PiggyBank, title: "See your savings", desc: "We show your EWS price and exactly how much you'd save every year." },
];

const TECH = [
  { icon: ScanLine, title: "Computer-Vision Capture", desc: "Our OCR vision engine reads any hauler's invoice in milliseconds and extracts your true recurring cost — no manual entry." },
  { icon: Scale, title: "Proprietary Weight Model", desc: "A custom-trained model benchmarks average waste weight by business type and container capacity to model real tonnage." },
  { icon: CloudRain, title: "Region & Climate Signals", desc: "It factors your region's seasonality and climate — even rainfall that soaks and adds weight inside your container." },
  { icon: Database, title: "Instant Database Math", desc: "Matched against our stored pricing dataset, the engine computes your savings in real time — mathematically, not guesswork." },
];

export default function SavingsCalculatorPage() {
  const { hash } = useLocation();
  const { t } = useI18n();
  const appJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "WiseWaste™ Commercial Waste Savings Calculator",
    url: "https://eewaste.com/savings-calculator",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description: t("sc.seoDesc"),
    provider: { "@id": "https://eewaste.com/#business" },
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  };
  useEffect(() => {
    if (hash === "#how-it-works") {
      const t = setTimeout(() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" }), 400);
      return () => clearTimeout(t);
    }
  }, [hash]);
  return (
    <>
      <Seo
        title={t("sc.seoTitle")}
        description={t("sc.seoDesc")}
        path="/savings-calculator"
        jsonLd={appJsonLd}
      />

      <section className="relative bg-[#0E3B3B] text-white pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 80% 10%, rgba(16,185,129,0.55), transparent 45%)" }} />
        <div className="relative z-[2] max-w-[1100px] mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center max-w-2xl mx-auto mb-12">
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-emerald-300/90">WiseWaste™ Savings Calculator</span>
            <h1 className="mt-3 font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl">
              See how much you&apos;d <span className="text-emerald-400">save</span> in seconds
            </h1>
            <p className="mt-5 text-white/75 leading-relaxed">
              No forms to fill in first. Just snap a photo of your current waste bill — our AI reads it and shows your EWS savings instantly.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
            <div className="space-y-6">
              {STEPS.map((s, i) => (
                <motion.div key={s.title} initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-4" data-testid={`calc-step-${i}`}>
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-emerald-500 text-[#0A2C2C] shrink-0"><s.icon size={22} /></span>
                  <div>
                    <h3 className="font-display font-bold text-lg">{s.title}</h3>
                    <p className="text-white/70 text-sm mt-1">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <SavingsCalculator variant="page" />
          </div>
        </div>
        <BottomWave />
      </section>

      {/* How the technology works — compact, techy, SEO-rich */}
      <section id="how-it-works" className="relative bg-[#FBFAF8] py-16 scroll-mt-24" data-testid="tech-section">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={16} className="text-emerald-600" />
            <span className="font-mono-tech text-[11px] uppercase tracking-[0.22em] text-emerald-700">Patent-Pending Technology</span>
          </div>
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8 items-start">
            <div>
              <h2 className="font-display font-extrabold tracking-tight leading-[1.08] text-2xl sm:text-3xl text-[#0B0F14]">
                Meet WiseWaste™ — <span className="text-emerald-600">our AI</span> behind your instant estimate
              </h2>
              <p className="mt-4 text-slate-600 text-sm leading-relaxed">
                Our savings engine is powered by a <span className="font-semibold text-[#0B0F14]">proprietary, in-house-trained AI</span> — not a generic
                calculator. We trained it on real-world waste data to model average container weight by <span className="font-semibold text-[#0B0F14]">business type</span>,
                <span className="font-semibold text-[#0B0F14]"> container capacity</span> and <span className="font-semibold text-[#0B0F14]">region</span>, then layer in
                seasonal and climate signals — including rainfall that soaks trash and quietly inflates your hauler&apos;s weight-based charges.
              </p>
              <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                The result: we capture, scan and instantly compute a data-backed estimate from our stored pricing database — helping businesses
                cut waste costs dramatically. EWS is proud to introduce this patent-pending technology to the waste-management industry.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {TECH.map((t, i) => (
                <motion.div key={t.title} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }}
                  className="bg-white border border-[#E7E5DF] rounded-2xl p-5 shadow-soft" data-testid={`tech-card-${i}`}>
                  <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-600/10 text-emerald-700 mb-3"><t.icon size={20} /></span>
                  <h3 className="font-display font-bold text-[15px] text-[#0B0F14]">{t.title}</h3>
                  <p className="mt-1.5 text-slate-600 text-[13px] leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
